import React from "react";
import styled from "styled-components";
import BathtubOutlinedIcon from "@material-ui/icons/BathtubOutlined";
import HotelOutlinedIcon from "@material-ui/icons/HotelOutlined";
import { MultiAvatar } from "@sonder/ui/source/styled/components/MultiAvatar";
import { colors } from "@sonder/ui/source/styled/theme";
import { TaskSchedulerMetrics } from "core/queries/GetSchedulerMetrics";
import { UserData } from "core/queries/GetUsers";

const mapAssignees = (totalAssignees: any) => {
  return totalAssignees.map((assignee: any) => ({
    imageUrl: (assignee as unknown as UserData).avatar?.url,
    ...assignee,
  }));
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: auto auto auto auto;
`;

const MultiAvatarWrapper = styled.h1`
  align-items: center;
  display: flex;
`;

const TopLevelMetricsColumn = styled.div`
  h1 {
    font-size: 3.23rem;
    line-height: 1;
    margin: 0 0 0.57rem 0;
  }

  h2 {
    font-size: 1.14rem;
    font-weight: 700;
    margin: 0 0 0.28rem;
  }

  div {
    font-size: 1rem;
    margin: 0 0 0.28rem 0;
  }
`;

const Subtitle = styled.div`
  color: ${colors.grayA};
`;

const StyledIconWrapper = styled.h1`
  > svg {
    font-size: 2.5rem;

    & + svg {
      margin-left: 0.5rem;
    }
  }
`;

const TotalScheduledCleans = styled.div<{ fullyScheduled?: boolean }>`
  color: ${props => (props.fullyScheduled ? colors.greenDark : colors.orangeD)};
`;

interface TotalMetricsProps {
  taskSchedulerMetrics: TaskSchedulerMetrics;
}

export const TotalMetrics = ({ taskSchedulerMetrics }: TotalMetricsProps) => {
  const {
    totalAssignees,
    totalBaths,
    totalBeds,
    totalCleans,
    totalCompletedCleans,
    totalHours,
    totalInProgressCleans,
    totalReadyCleans,
    totalScheduledCleans,
    totalUnscheduledCleans,
  } = taskSchedulerMetrics;

  const avatars = mapAssignees(totalAssignees);

  return (
    <Wrapper>
      <TopLevelMetricsColumn>
        <h1>{totalCompletedCleans}</h1>
        <h2>Cleanings completed</h2>
        {!!totalInProgressCleans && (
          <div>{totalInProgressCleans} In progress</div>
        )}
        {!!totalReadyCleans && <div>{totalReadyCleans} Not started</div>}
      </TopLevelMetricsColumn>

      <TopLevelMetricsColumn>
        <h1>{totalCleans}</h1>
        <h2>Cleans</h2>
        <TotalScheduledCleans
          fullyScheduled={totalCleans === totalScheduledCleans}
        >
          {totalScheduledCleans} / {totalCleans} Scheduled
        </TotalScheduledCleans>
        <Subtitle>{totalUnscheduledCleans} unscheduled</Subtitle>
      </TopLevelMetricsColumn>

      <TopLevelMetricsColumn>
        <MultiAvatarWrapper>
          <MultiAvatar avatars={avatars} maxLength={4} />
          {"\u00A0"}
        </MultiAvatarWrapper>
        <h2>{totalAssignees.length} assigned</h2>
      </TopLevelMetricsColumn>

      <TopLevelMetricsColumn>
        <StyledIconWrapper>
          <HotelOutlinedIcon />
          <BathtubOutlinedIcon />
        </StyledIconWrapper>
        <h2>
          {totalBeds} beds | {totalBaths} baths
        </h2>
        <Subtitle>{totalHours.toFixed(2)} total estimated hours</Subtitle>
      </TopLevelMetricsColumn>
    </Wrapper>
  );
};
